import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { hazardMitigationData } from '../data/hazardMitigationData';
import './HazardMitigation.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

const PRIORITIES = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];

const SCHEDULES = [
  { value: 'Short Term', label: 'Short Term' },
  { value: 'Medium Term', label: 'Medium Term' },
  { value: 'Long Term', label: 'Long Term' },
];

function priorityClass(p) {
  if (p === 'High') return 'hmp-prio-high';
  if (p === 'Medium') return 'hmp-prio-medium';
  if (p === 'Low') return 'hmp-prio-low';
  return '';
}

function scheduleClass(term) {
  if (term === 'Short Term') return 'hmp-sched-short';
  if (term === 'Medium Term') return 'hmp-sched-medium';
  if (term === 'Long Term') return 'hmp-sched-long';
  return '';
}

function getAllActions(data) {
  const acts = [];
  data.goals.forEach(g => g.actions.forEach(a => acts.push(a)));
  return acts;
}

function HazardMitigation() {
  const [sel, setSel] = useState({ priority: new Set(), schedule: new Set() });
  const [expandedGoals, setExpandedGoals] = usePersistentSet('hazard-mitigation:expandedGoals', () => new Set(hazardMitigationData.goals.map(g => g.id)));
  const [expandedActions, setExpandedActions] = usePersistentSet('hazard-mitigation:expandedActions');
  const [overviewExpanded, setOverviewExpanded] = usePersistentFlag('hazard-mitigation:overviewExpanded');
  const [keyExpanded, setKeyExpanded] = usePersistentFlag('hazard-mitigation:keyExpanded');

  useEffect(() => {
    document.title = 'Hazard Mitigation Plan';
  }, []);

  const allActions = useMemo(() => getAllActions(hazardMitigationData), []);

  const dimensions = useMemo(() => [
    { key: 'priority', label: 'Priority', values: PRIORITIES.map(p => ({
      value: p.value, label: p.label,
      count: allActions.filter(a => a.priority === p.value).length,
    })) },
    { key: 'schedule', label: 'Implementation Schedule', values: SCHEDULES.map(s => ({
      value: s.value, label: s.label,
      count: allActions.filter(a => a.scheduleTerm === s.value).length,
    })) },
  ], [allActions]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ priority: new Set(), schedule: new Set() });

  const matchesAction = useMemo(() => {
    return (a) => {
      if (sel.priority.size && !sel.priority.has(a.priority)) return false;
      if (sel.schedule.size && !sel.schedule.has(a.scheduleTerm)) return false;
      return true;
    };
  }, [sel]);

  const filteredGoals = useMemo(() => {
    return hazardMitigationData.goals.map(g => ({
      ...g,
      actions: g.actions.filter(matchesAction)
    })).filter(g => g.actions.length > 0);
  }, [matchesAction]);

  const totalFiltered = useMemo(() => {
    return filteredGoals.reduce((sum, g) => sum + g.actions.length, 0);
  }, [filteredGoals]);

  const toggleGoal = (id) => {
    setExpandedGoals(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAction = (num) => {
    setExpandedActions(prev => {
      const next = new Set(prev);
      if (next.has(num)) next.delete(num); else next.add(num);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGoals(new Set(hazardMitigationData.goals.map(g => g.id)));
    setExpandedActions(new Set(allActions.map(a => a.num)));
    setOverviewExpanded(true);
    setKeyExpanded(true);
  };

  const collapseAll = () => {
    setExpandedGoals(new Set());
    setExpandedActions(new Set());
    setOverviewExpanded(false);
    setKeyExpanded(false);
  };

  const renderMetaField = (label, value) => (
    value ? (
      <div className="hmp-meta-field">
        <span className="hmp-meta-label">{label}:</span> {value}
      </div>
    ) : null
  );

  return (
    <div className="hmp-page">
      <a href="#main-content" className="hmp-skip-link">Skip to main content</a>
      <Header
        title="Hazard Mitigation Plan"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="hmp-content">
        <div className="hmp-intro">
          <p className="hmp-updated">
            Town of Brookline · Hazard Mitigation Plan {hazardMitigationData.planYear} · FEMA-approved
          </p>
        </div>

        <div className="hmp-controls">
          <FilterBar
            allCount={allActions.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="hmp-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="hmp-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFiltered}
          noun="action"
          nounPlural="actions"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="hmp-preamble-sections">
          <div className="hmp-preamble">
            <button
              className={`hmp-preamble-header ${overviewExpanded ? 'expanded' : ''}`}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
              aria-expanded={overviewExpanded}
            >
              <span className={`hmp-chevron${overviewExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="hmp-preamble-title">About This Plan</span>
            </button>
            {overviewExpanded && (
              <div className="hmp-preamble-body">
                {hazardMitigationData.introduction.overview.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="hmp-preamble">
            <button
              className={`hmp-preamble-header ${keyExpanded ? 'expanded' : ''}`}
              onClick={() => setKeyExpanded(!keyExpanded)}
              aria-expanded={keyExpanded}
            >
              <span className={`hmp-chevron${keyExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="hmp-preamble-title">Priority &amp; Schedule Key</span>
            </button>
            {keyExpanded && (
              <div className="hmp-preamble-body">
                {hazardMitigationData.introduction.itemsNote.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
                <ul className="hmp-key-list">
                  {hazardMitigationData.legend.priority.map(p => (
                    <li key={p.code}>
                      <span className={`hmp-prio-badge ${priorityClass(p.code)}`}>{p.code}</span>
                      <span className="hmp-key-label">{p.label}</span>
                    </li>
                  ))}
                </ul>
                <ul className="hmp-key-list">
                  {hazardMitigationData.legend.schedule.map(s => (
                    <li key={s.code}>
                      <span className={`hmp-sched-badge ${scheduleClass(s.code)}`}>{s.code}</span>
                      <span className="hmp-key-label">{s.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="hmp-goals">
          {filteredGoals.map(goal => (
            <div key={goal.id} className="hmp-goal">
              <h2 className="hmp-heading"><button
                className={`hmp-goal-header ${expandedGoals.has(goal.id) ? 'expanded' : ''}`}
                onClick={() => toggleGoal(goal.id)}
                aria-expanded={expandedGoals.has(goal.id)}
              >
                <span className={`hmp-chevron hmp-chevron-light${expandedGoals.has(goal.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                <div className="hmp-goal-header-text">
                  <span className="hmp-goal-name">Goal {goal.number}: {goal.name}</span>
                  {goal.statement && (
                    <span className="hmp-goal-desc">{goal.statement}</span>
                  )}
                </div>
                <span className="hmp-goal-count" aria-hidden="true">{goal.actions.length}</span>
              </button></h2>

              {expandedGoals.has(goal.id) && (
                <div className="hmp-goal-body">
                  {goal.actions.map(a => {
                    const isExpanded = expandedActions.has(a.num);
                    return (
                      <div key={a.num} className="hmp-action">
                        <h3 className="hmp-heading"><button
                          className={`hmp-action-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleAction(a.num)}
                          aria-expanded={isExpanded}
                        >
                          <span className={`hmp-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                          <span className="hmp-action-id">{a.num}</span>
                          <span className="hmp-action-title">{a.title}</span>
                          <span className={`hmp-prio-badge small ${priorityClass(a.priority)}`}>{a.priority}</span>
                        </button></h3>
                        {isExpanded && (
                          <div className="hmp-action-body">
                            {a.description && <p className="hmp-action-desc">{a.description}</p>}
                            <div className="hmp-action-meta">
                              {renderMetaField('Priority', a.priority)}
                              {renderMetaField('Hazards addressed', a.hazards)}
                              {renderMetaField('Action lead', a.lead)}
                              {renderMetaField('Supporting agencies', a.supporting)}
                              {renderMetaField('Estimated cost', a.cost)}
                              {renderMetaField('Potential funding', a.funding)}
                              {renderMetaField('Implementation schedule', a.schedule)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="hmp-source">
          <p>
            Source: Town of Brookline, MA Hazard Mitigation Plan {hazardMitigationData.planYear} (FEMA-approved
            November 28, 2023). This page presents the plan’s Mitigation Action Plan (Chapter 6) — 59 actions
            organized by the plan’s five mitigation goals (Table 79), with each action’s detail from Table 74; the
            risk assessment, capability assessment, and appendices are in the source document.
          </p>
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/3532/Hazard-Mitigation-Plan-2023-Update" target="_blank" rel="noopener noreferrer">
              Hazard Mitigation Plan<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default HazardMitigation;
